import type { ArrayElement } from '@mythos/utils/ts_utils';
import type { ExecutionResult } from 'graphql';
import { useEffect, useState } from 'react';
import { graphql } from './generated/gql';
import type {
  GetLobbyListQuery,
  GetLobbyQuery,
  TypedDocumentString,
  UserSummaryFragment,
} from './generated/gql/graphql';

export type User = UserSummaryFragment;

/**
 * Extract and unwrap a field from a query result type.
 * Usage: QueryField<GetUserProfileQuery, 'user'> → User type
 */
export type QueryField<T, K extends keyof T> = NonNullable<T[K]>;

/**
 * Lobby type derived from GetLobbyQuery
 */
export type Lobby = QueryField<GetLobbyQuery, 'lobby'>;

/**
 * Lobby list item type derived from GetLobbyListQuery
 */
export type LobbyListItem = ArrayElement<GetLobbyListQuery['lobby_list']>;

export type UserWithColor = UserSummaryFragment & {
  color: string;
};

export const UserSummaryFragmentDoc = graphql(`
  fragment UserSummary on User {
    id
    createdAt
    name
    profilePictureURL
    profileURL
    subtitle
    stats {
      rating
      totalGames
      totalWins
      totalBlameMillis
    }
  }
`);

export const LobbyListQuery = graphql(`
  query getLobbyList {
    lobby_list {
      id
      name
      host {
        ...UserSummary
      }
      creationDate
      status
      game {
        id
        endTimestamp
      }
      players {
        edges {
          node {
            ...UserSummary
          }
        }
      }
    }
  }
`);

export const LobbyQuery = graphql(`
  query getLobby($id: ID!) {
    lobby(id: $id) {
      id
      sequenceID
      name
      options
      modules
      host {
        ...UserSummary
      }
      players {
        edges {
          node {
            ...UserSummary
          }
        }
      }
      playerIDToBotType {
        playerID
        botType
      }
      game {
        id
      }
    }
  }
`);

export const UsersQuery = graphql(`
  query Users($ids: [ID!]!) {
    users(ids: $ids) {
      ...UserSummary
    }
  }
`);

export async function executeGQL<TResult, TVariables>(
  query: TypedDocumentString<TResult, TVariables>,
  ...[variables]: TVariables extends Record<string, never> ? [] : [TVariables]
) {
  const response = await fetch('/api/graphql', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/graphql-response+json',
    },
    body: JSON.stringify({
      query,
      variables,
    }),
  });

  if (!response.ok) {
    throw new Error('Network response was not ok');
  }

  return response.json() as ExecutionResult<TResult>;
}

export type QueryState<T> =
  | { status: 'loading'; data: null; error: null }
  | { status: 'error'; data: null; error: Error }
  | { status: 'success'; data: T; error: null };

export function useGraphQLQuery<TResult, TVariables>(
  query: TypedDocumentString<TResult, TVariables>,
  variables: TVariables = null as TVariables,
  options?: { refreshMs?: number },
): QueryState<TResult> {
  const [state, setState] = useState<QueryState<TResult>>({
    status: 'loading',
    data: null,
    error: null,
  });
  useEffect(() => {
    const fetchData = () => {
      // @ts-ignore
      executeGQL(query, variables)
        .then((result) => {
          if (result.errors) {
            setState({
              status: 'error',
              data: null,
              error: new Error(result.errors[0].message),
            });
          } else if (result.data) {
            setState({ status: 'success', data: result.data, error: null });
          }
        })
        .catch((error) => {
          setState({ status: 'error', data: null, error });
        });
    };

    setState({ status: 'loading', data: null, error: null });
    fetchData();

    if (options?.refreshMs) {
      const interval = setInterval(fetchData, options.refreshMs);
      return () => clearInterval(interval);
    }
  }, [query, variables, options?.refreshMs]);
  return state;
}
